import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import "@/App.css";
import { AuthProvider, useAuth } from "@/lib/auth";

import Login from "@/pages/Login";
import Register from "@/pages/Register";
import AuthCallback from "@/pages/AuthCallback";

// Public pages
import PublicLayout from "@/components/PublicLayout";
import Landing from "@/pages/public/Landing";
import ChatAdvisor from "@/pages/public/ChatAdvisor";
import GuideHub from "@/pages/public/GuideHub";
import PermitCheck from "@/pages/public/PermitCheck";
import PermitWizard from "@/pages/public/PermitWizard";
import PropertyAnalysis from "@/pages/public/PropertyAnalysis";
import Templates from "@/pages/public/Templates";
import DrawingReview from "@/pages/public/DrawingReview";
import MunicipalityFinder from "@/pages/public/MunicipalityFinder";
import SharedProject from "@/pages/public/SharedProject";
import ReportSupervision from "@/pages/public/ReportSupervision";

// App (authed) pages
import Dashboard from "@/pages/Dashboard";
import CitizenHome from "@/pages/CitizenHome";
import Projects from "@/pages/Projects";
import ApplicationWizard from "@/pages/ApplicationWizard";
import Messages from "@/pages/Messages";
import MyHandlaggare from "@/pages/MyHandlaggare";
import Cases from "@/pages/Cases";
import CaseDetail from "@/pages/CaseDetail";
import Documents from "@/pages/Documents";
import Contacts from "@/pages/Contacts";
import Profile from "@/pages/Profile";
import Admin from "@/pages/Admin";
import Tillsyn from "@/pages/Tillsyn";
import TillsynDetail from "@/pages/TillsynDetail";
import DrawingCreate from "@/pages/DrawingCreate";
import SignupOrg from "@/pages/SignupOrg";
import BillingSuccess from "@/pages/BillingSuccess";
import BillingManage from "@/pages/BillingManage";
import SuperAdmin from "@/pages/SuperAdmin";
import AuditLog from "@/pages/AuditLog";
import MailImport from "@/pages/MailImport";
import SanctionCalculator from "@/pages/SanctionCalculator";
import AiStats from "@/pages/AiStats";
import HandlaggareLogin from "@/pages/HandlaggareLogin";
import Terms from "@/pages/public/Terms";
import ForKommuner from "@/pages/public/ForKommuner";
import Privacy from "@/pages/public/Privacy";
import CookieConsent from "@/components/CookieConsent";
import GoogleAnalytics from "@/components/GoogleAnalytics";
import Pricing from "@/pages/public/Pricing";
import RuleCategory from "@/pages/rulebank/RuleCategory";
import RulePage from "@/pages/rulebank/RulePage";
import ChatShared from "@/pages/ChatShared";
import Blogg from "@/pages/public/Blogg";
import BloggPost from "@/pages/public/BloggPost";
import LawBrowser from "@/pages/LawBrowser";
import BbrTables from "@/pages/BbrTables";
import AppShell from "@/components/AppShell";
import OsCases from "@/pages/os/OsCases";
import OsCaseDetail from "@/pages/os/OsCaseDetail";
import OsProperties from "@/pages/os/OsProperties";
import OsMapView from "@/pages/os/OsMapView";
import { QuotaInterceptor } from "@/components/QuotaInterceptor";
import { Loader2 } from "lucide-react";

const ADMIN_ROLES = ["admin"];
const USER_ROLES = ["user"];
const PLATFORM_ROLES = ["platform_admin"];

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-5 h-5 animate-spin text-zinc-500" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "platform_admin") return <Navigate to="/super-admin" replace />;
  return children;
}

function PlatformProtected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-zinc-500" /></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "platform_admin") return <Navigate to="/dashboard" replace />;
  return children;
}

function RoleRoute({ allow, children }) {
  const { user } = useAuth();
  if (!allow.includes(user?.role)) {
    return <Navigate to={user?.role === "admin" ? "/dashboard" : "/projects"} replace />;
  }
  return children;
}

function HemRedirect() {
  return <Navigate to="/projects" replace />;
}

function Router() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <>
      <QuotaInterceptor />
      <CookieConsent />
      <GoogleAnalytics />
      <Routes>
      {/* Auth screens (no layout) */}
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/handlaggare" element={<HandlaggareLogin />} />
      <Route path="/handlaggare/login" element={<HandlaggareLogin />} />
      <Route path="/signup-org" element={<SignupOrg />} />
      <Route path="/billing/success" element={<BillingSuccess />} />
      <Route path="/super-admin" element={<PlatformProtected><SuperAdmin /></PlatformProtected>} />

      {/* Public layout (no auth required) */}
      <Route element={<PublicLayout />}>
        <Route path="/" element={<Landing />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/chat" element={<ChatAdvisor />} />
        <Route path="/guide" element={<GuideHub />} />
        <Route path="/guide/permit" element={<PermitCheck />} />
        <Route path="/guide/wizard" element={<PermitWizard />} />
        <Route path="/regler" element={<PermitWizard />} />
        <Route path="/guide/property" element={<PropertyAnalysis />} />
        <Route path="/guide/templates" element={<Templates />} />
        <Route path="/guide/review" element={<DrawingReview />} />
        <Route path="/municipality" element={<MunicipalityFinder />} />
        <Route path="/anmal-tillsyn" element={<ReportSupervision />} />
        <Route path="/shared/:token" element={<SharedProject />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/for-kommuner" element={<ForKommuner />} />
        <Route path="/privacy" element={<Privacy />} />
        {/* Regelbanken – SEO-optimerad content hub (explicita routes för att undvika kollisioner) */}
        <Route path="/pbl" element={<RuleCategory />} />
        <Route path="/pbl/:slug" element={<RulePage />} />
        <Route path="/bbr" element={<RuleCategory />} />
        <Route path="/bbr/:slug" element={<RulePage />} />
        <Route path="/attefall" element={<RuleCategory />} />
        <Route path="/attefall/:slug" element={<RulePage />} />
        <Route path="/strandskydd" element={<RuleCategory />} />
        <Route path="/strandskydd/:slug" element={<RulePage />} />
        <Route path="/bygglov" element={<RuleCategory />} />
        <Route path="/bygglov/:slug" element={<RulePage />} />
        {/* Alias för menytexten Regelbanken → landar på bygglov-hubben */}
        <Route path="/regelbanken" element={<Navigate to="/bygglov" replace />} />
        <Route path="/detaljplan" element={<RuleCategory />} />
        <Route path="/detaljplan/:slug" element={<RulePage />} />
        <Route path="/va" element={<RuleCategory />} />
        <Route path="/va/:slug" element={<RulePage />} />
        <Route path="/chattdelad/:token" element={<ChatShared />} />
        <Route path="/blogg" element={<Blogg />} />
        <Route path="/blogg/:slug" element={<BloggPost />} />
        {/* Lag-fulltextläsare (verifierad från Riksdagen) */}
        <Route path="/lag/:lawSlug" element={<LawBrowser />} />
        <Route path="/lag/:lawSlug/:chapter" element={<LawBrowser />} />
        <Route path="/bbr-krav" element={<BbrTables />} />
      </Route>

      {/* App layout (requires login) */}
      <Route element={<Protected><AppShell /></Protected>}>
        <Route path="/os" element={<OsCases />} />
        <Route path="/os/cases/:id" element={<OsCaseDetail />} />
        <Route path="/os/properties" element={<OsProperties />} />
        <Route path="/os/map" element={<OsMapView />} />
        {/* Citizen routes */}
        <Route path="/hem" element={<HemRedirect />} />
        <Route path="/projects" element={<RoleRoute allow={USER_ROLES}><Projects /></RoleRoute>} />
        <Route path="/ansok" element={<RoleRoute allow={USER_ROLES}><ApplicationWizard /></RoleRoute>} />
        <Route path="/messages" element={<RoleRoute allow={USER_ROLES}><Messages /></RoleRoute>} />
        <Route path="/min-handlaggare" element={<RoleRoute allow={USER_ROLES}><MyHandlaggare /></RoleRoute>} />
        <Route path="/tools/draw" element={<RoleRoute allow={USER_ROLES}><DrawingCreate /></RoleRoute>} />

        {/* Admin routes */}
        <Route path="/dashboard" element={<RoleRoute allow={ADMIN_ROLES}><Dashboard /></RoleRoute>} />
        <Route path="/documents" element={<RoleRoute allow={ADMIN_ROLES}><Documents /></RoleRoute>} />
        <Route path="/contacts" element={<RoleRoute allow={ADMIN_ROLES}><Contacts /></RoleRoute>} />
        <Route path="/admin" element={<RoleRoute allow={ADMIN_ROLES}><Admin /></RoleRoute>} />
        <Route path="/admin/ai-stats" element={<RoleRoute allow={ADMIN_ROLES}><AiStats /></RoleRoute>} />
        <Route path="/tillsyn" element={<RoleRoute allow={ADMIN_ROLES}><Tillsyn /></RoleRoute>} />
        <Route path="/tillsyn/:tillsyn_id" element={<RoleRoute allow={ADMIN_ROLES}><TillsynDetail /></RoleRoute>} />
        <Route path="/audit-log" element={<RoleRoute allow={ADMIN_ROLES}><AuditLog /></RoleRoute>} />
        <Route path="/mail-import" element={<RoleRoute allow={ADMIN_ROLES}><MailImport /></RoleRoute>} />
        <Route path="/sanction" element={<RoleRoute allow={ADMIN_ROLES}><SanctionCalculator /></RoleRoute>} />

        {/* Shared */}
        <Route path="/cases" element={<Cases />} />
        <Route path="/cases/:case_id" element={<CaseDetail />} />
        <Route path="/profile" element={<Profile />} />
        <Route path="/billing/plans" element={<RoleRoute allow={ADMIN_ROLES}><Pricing /></RoleRoute>} />
        <Route path="/billing/manage" element={<RoleRoute allow={ADMIN_ROLES}><BillingManage /></RoleRoute>} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Router />
          <Toaster position="top-right" richColors closeButton />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
